
import React, { useMemo, useState, useEffect } from 'react';
import { SystemTransaction } from '@/shared/types';
import { TrendingUp, Check, DollarSign, CreditCard, Users, ArrowUpRight, ArrowDownRight, FileText, Calendar, AlertCircle } from 'lucide-react';
import SimpleChart from '@/features/settings/components/SimpleChart';
import { adminServiceAPI, MonthlyRevenue, OutstandingInvoice, RecentPayment } from '@/features/admin/services/adminService-api';

const AdminFinanceView: React.FC = () => {
    const [transactions, setTransactions] = useState<SystemTransaction[]>([]);
    const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);
    const [outstandingInvoices, setOutstandingInvoices] = useState<OutstandingInvoice[]>([]);
    const [recentPayments, setRecentPayments] = useState<RecentPayment[]>([]);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<'30d' | '90d' | '1y' | 'all'>('90d');

    useEffect(() => {
        loadFinanceData();
    }, []);

    const loadFinanceData = async () => {
        try {
            setLoading(true);
            const [revenueData, transactionsData, outstandingData, paymentsData] = await Promise.all([
                adminServiceAPI.getMonthlyRevenue(),
                adminServiceAPI.getSystemTransactions(),
                adminServiceAPI.getOutstandingInvoices(),
                adminServiceAPI.getRecentPayments()
            ]);

            setMonthlyRevenue(revenueData);
            setTransactions(transactionsData);
            setOutstandingInvoices(outstandingData);
            setRecentPayments(paymentsData);
        } catch (error) {
            console.error('Error loading finance data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate metrics
    const metrics = useMemo(() => {
        const totalRevenue = transactions
            .filter(tx => tx.status === 'succeeded')
            .reduce((sum, tx) => sum + tx.amount, 0);

        // Calculate MRR from active subscriptions
        const mrr = transactions
            .filter(tx => tx.status === 'succeeded' && tx.planId !== 'payment')
            .reduce((sum, tx) => {
                const planAmounts: Record<string, number> = {
                    'basic': 1,
                    'pro': 10,
                    'enterprise': 29,
                    'free': 0
                };
                const planId = tx.planId && tx.planId in planAmounts ? tx.planId : 'free';
                return sum + planAmounts[planId];
            }, 0);

        const payingUsers = new Set(transactions.map(tx => tx.userEmail)).size;
        const arpu = payingUsers > 0 ? mrr / payingUsers : 0;

        // Calculate outstanding from real invoices
        const outstanding = outstandingInvoices.reduce((sum, inv) => sum + inv.amount, 0);

        return {
            totalRevenue,
            mrr,
            arpu,
            payingUsers,
            outstanding
        };
    }, [transactions, outstandingInvoices]);

    const revenueData = useMemo(() => {
        if (monthlyRevenue.length > 0) {
            return {
                data: monthlyRevenue.map(m => m.revenue),
                labels: monthlyRevenue.map(m => m.month)
            };
        }

        // Fallback mock data
        const timeRanges = {
            '30d': { data: [850, 920, 1100], labels: ['Week 1', 'Week 2', 'Week 3'] },
            '90d': { data: [650, 720, 850, 920, 1100, 1250], labels: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'] },
            '1y': { data: [200, 350, 450, 550, 650, 720, 850, 920, 1100, 1250, 1400, 1600], labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
            'all': { data: [120, 300, 450, 600, 850, 920, 1100, 1250, 1400, 1600, 1850, 2100], labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] }
        };

        return timeRanges[timeRange];
    }, [monthlyRevenue, timeRange]);

    const planStats = useMemo(() => {
        const counts: Record<string, number> = { free: 0, basic: 0, pro: 0, enterprise: 0 };
        transactions.forEach(tx => {
            if (tx.planId && tx.planId in counts) {
                counts[tx.planId] = (counts[tx.planId] || 0) + 1;
            }
        });
        return counts;
    }, [transactions]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-5 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Financial Overview</h2>
                    <p className="text-gray-500 text-sm">Monitor system revenue, payments, and outstanding invoices.</p>
                </div>
                <div className="flex gap-3">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value as any)}
                        className="px-3 py-2 border border-gray-300 rounded text-sm focus:border-gray-400 focus:outline-none bg-white text-gray-800 transition-colors"
                    >
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="1y">Last year</option>
                        <option value="all">All time</option>
                    </select>
                    <button className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded font-medium text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors">
                        <FileText size={14} />
                        Export Report
                    </button>
                </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Revenue */}
                <div className="bg-white p-4 rounded border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-emerald-100 text-emerald-700 rounded">
                            <DollarSign size={16} />
                        </div>
                        <div className="flex items-center text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-0.5 rounded">
                            <ArrowUpRight size={12} />
                            +15%
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Total Revenue</p>
                        <h3 className="text-xl font-semibold text-gray-900 mt-1">${metrics.totalRevenue.toLocaleString()}</h3>
                    </div>
                </div>

                {/* MRR */}
                <div className="bg-white p-4 rounded border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-indigo-100 text-indigo-700 rounded">
                            <Calendar size={16} />
                        </div>
                        <div className="flex items-center text-emerald-600 text-xs font-medium bg-emerald-50 px-2 py-0.5 rounded">
                            <ArrowUpRight size={12} />
                            +8%
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">MRR</p>
                        <h3 className="text-xl font-semibold text-gray-900 mt-1">${metrics.mrr.toLocaleString()}</h3>
                        <p className="text-xs text-gray-400 mt-1">Monthly recurring revenue</p>
                    </div>
                </div>

                {/* ARPU */}
                <div className="bg-white p-4 rounded border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-purple-100 text-purple-700 rounded">
                            <Users size={16} />
                        </div>
                        <div className="flex items-center text-gray-400 text-xs font-medium bg-gray-100 px-2 py-0.5 rounded">
                            Stable
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">ARPU</p>
                        <h3 className="text-xl font-semibold text-gray-900 mt-1">${metrics.arpu.toFixed(2)}</h3>
                        <p className="text-xs text-gray-400 mt-1">Avg revenue per user</p>
                    </div>
                </div>

                {/* Outstanding */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded border border-red-200">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-red-200 text-red-700 rounded">
                            <AlertCircle size={16} />
                        </div>
                        <div className="flex items-center text-red-600 text-xs font-medium bg-red-100 px-2 py-0.5 rounded">
                            <ArrowDownRight size={12} />
                            -5%
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-red-700 uppercase">Outstanding</p>
                        <h3 className="text-xl font-semibold text-red-900 mt-1">${metrics.outstanding.toLocaleString()}</h3>
                        <p className="text-xs text-red-700 mt-1">{outstandingInvoices.length} invoices overdue</p>
                    </div>
                </div>
            </div>

            {/* Revenue Chart and Plan Distribution */}
            <div className="grid lg:grid-cols-3 gap-5">
                {/* Chart Section */}
                <div className="lg:col-span-2 bg-white p-5 rounded border border-gray-200">
                    <div className="flex justify-between items-center mb-5">
                        <div>
                            <h3 className="font-semibold text-gray-900 text-sm">Revenue Trend</h3>
                            <p className="text-xs text-gray-500">Monthly income growth</p>
                        </div>
                        <div className="flex gap-2">
                            <span className="w-2 h-2 rounded bg-indigo-600"></span>
                            <span className="text-xs font-medium text-gray-500">Revenue</span>
                        </div>
                    </div>
                    <SimpleChart data={revenueData.data} labels={revenueData.labels} color="#4f46e5" height={250} />
                </div>

                {/* Plan Distribution */}
                <div className="bg-white p-5 rounded border border-gray-200">
                    <h3 className="font-semibold text-gray-900 text-sm mb-4">Plan Breakdown</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded bg-indigo-600"></div>
                                <span className="font-medium text-gray-700 text-sm">Pro Plan</span>
                            </div>
                            <span className="font-medium text-gray-900 text-sm">{planStats.pro ?? 0}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded h-1.5">
                            <div
                                className="bg-indigo-600 h-1.5 rounded"
                                style={{ width: `${((planStats.pro ?? 0) / (transactions.length || 1)) * 100}%` }}
                            ></div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded bg-blue-500"></div>
                                <span className="font-medium text-gray-700 text-sm">Basic Plan</span>
                            </div>
                            <span className="font-medium text-gray-900 text-sm">{planStats.basic ?? 0}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded h-1.5">
                            <div
                                className="bg-blue-500 h-1.5 rounded"
                                style={{ width: `${((planStats.basic ?? 0) / (transactions.length || 1)) * 100}%` }}
                            ></div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded bg-purple-600"></div>
                                <span className="font-medium text-gray-700 text-sm">Enterprise Plan</span>
                            </div>
                            <span className="font-medium text-gray-900 text-sm">{planStats.enterprise ?? 0}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded h-1.5">
                            <div
                                className="bg-purple-600 h-1.5 rounded"
                                style={{ width: `${((planStats.enterprise ?? 0) / (transactions.length || 1)) * 100}%` }}
                            ></div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded bg-gray-400"></div>
                                <span className="font-medium text-gray-700 text-sm">Free Tier</span>
                            </div>
                            <span className="font-medium text-gray-900 text-sm">{planStats.free ?? 0}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded h-1.5">
                            <div
                                className="bg-gray-400 h-1.5 rounded"
                                style={{ width: `${((planStats.free ?? 0) / (transactions.length || 1)) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Outstanding Invoices */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900 text-sm">Outstanding Invoices</h3>
                    <div className="flex items-center gap-1 text-red-600 text-xs font-medium">
                        <AlertCircle size={12} />
                        {outstandingInvoices.length} overdue
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-red-50 border-b border-red-200">
                            <tr>
                                <th className="px-4 py-3 text-xs font-medium text-red-700 uppercase">Invoice #</th>
                                <th className="px-4 py-3 text-xs font-medium text-red-700 uppercase">Client</th>
                                <th className="px-4 py-3 text-xs font-medium text-red-700 uppercase">Amount</th>
                                <th className="px-4 py-3 text-xs font-medium text-red-700 uppercase">Due Date</th>
                                <th className="px-4 py-3 text-xs font-medium text-red-700 uppercase">Days Overdue</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {outstandingInvoices.map((invoice) => (
                                <tr key={invoice.id} className="hover:bg-red-50/50 transition-colors">
                                    <td className="px-4 py-3 font-mono text-gray-700 font-medium">{invoice.invoiceNumber}</td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{invoice.clientName}</td>
                                    <td className="px-4 py-3 font-medium text-red-600">${invoice.amount.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-gray-500">{formatDate(invoice.dueDate)}</td>
                                    <td className="px-4 py-3">
                                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                                            {invoice.daysOverdue} days
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent Payments */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900 text-sm">Recent Payments</h3>
                    <button className="text-indigo-600 font-medium text-xs flex items-center gap-1 hover:underline">
                        View All <ArrowUpRight size={12} />
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase">
                            <tr>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Client</th>
                                <th className="px-4 py-3">Method</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {recentPayments.map((payment) => (
                                <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-gray-500">{formatDate(payment.date)}</td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{payment.clientName}</td>
                                    <td className="px-4 py-3 text-gray-500">{payment.method}</td>
                                    <td className="px-4 py-3">
                                        {payment.status === 'succeeded' ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium text-xs bg-emerald-100 px-2 py-0.5 rounded">
                                                <Check size={10} />
                                                Paid
                                            </span>
                                        ) : payment.status === 'pending' ? (
                                            <span className="text-amber-600 font-medium text-xs bg-amber-100 px-2 py-0.5 rounded">
                                                Pending
                                            </span>
                                        ) : (
                                            <span className="text-red-600 font-medium text-xs bg-red-100 px-2 py-0.5 rounded">
                                                Failed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                                        ${payment.amount.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminFinanceView;
